import { UsersRound } from 'lucide-react'

import { useState } from 'react'

import { type Branch, BranchModel } from '@/http/models/Branch'
import type { ResourceApi } from '@/http/models/Resource'

import { Badge } from '@/components/ui/badge'

import { BranchStaffDialog } from '@/components/features/pos/BranchStaffDialog'
import { ResourcePage } from '@/components/features/pos/ResourcePage'

const api: ResourceApi<Branch> = { getList: BranchModel.getList, create: BranchModel.create, update: BranchModel.update, remove: BranchModel.remove }

const Branches = () => {
  const [staffOf, setStaffOf] = useState<Branch | null>(null)
  const [refresh, setRefresh] = useState<(() => void) | null>(null)

  return (
    <>
      <ResourcePage<Branch>
        title="Branches"
        description="Your shops and warehouses. Each terminal belongs to one branch."
        noun="branch"
        api={api}
        permissions={{ create: 'branches:create', update: 'branches:update', delete: 'branches:delete' }}
        searchPlaceholder="Search by name"
        graph={{ '*': true, staffs: { _id: true, name: true, email: true, phone: true } }}
        columns={[
          {
            label: 'Name',
            render: (b) => (
              <span className="flex items-center gap-2 font-medium text-ink">
                {b.name}
                {b.is_main && <Badge tone="info">Main</Badge>}
              </span>
            ),
          },
          { label: 'Type', render: (b) => (b.type === 'warehouse' ? 'Warehouse' : 'Branch'), className: 'hidden md:table-cell' },
          { label: 'Address', render: (b) => b.address, className: 'hidden lg:table-cell' },
          { label: 'Phone', render: (b) => b.contact_number || '—', className: 'hidden md:table-cell' },
          { label: 'Staff', render: (b) => <span className="tabular-nums">{b.staffs?.length ?? b.staff_ids?.length ?? 0}</span>, className: 'text-right' },
          { label: 'Status', render: (b) => <Badge tone={b.status === 'active' ? 'success' : 'soft'}>{b.status === 'active' ? 'Active' : 'Inactive'}</Badge> },
        ]}
        fields={[
          { name: 'name', label: 'Name', type: 'text', required: true },
          {
            name: 'type',
            label: 'Type',
            type: 'select',
            options: [
              { label: 'Branch', value: 'branch' },
              { label: 'Warehouse', value: 'warehouse' },
            ],
          },
          { name: 'address', label: 'Address', type: 'textarea', required: true, help: 'At least 5 characters.' },
          { name: 'contact_number', label: 'Phone', type: 'text' },
          { name: 'email', label: 'Email', type: 'email' },
          { name: 'is_main', label: 'Main branch', type: 'switch', help: 'Only one branch is the main one.' },
          { name: 'status', label: 'Active', type: 'status' },
        ]}
        toForm={(b) => ({
          name: b?.name ?? '',
          type: b?.type ?? 'branch',
          address: b?.address ?? '',
          contact_number: b?.contact_number ?? '',
          email: b?.email ?? '',
          is_main: b?.is_main ?? false,
          status: (b?.status ?? 'active') === 'active',
        })}
        rowActions={(b, reload) => [
          {
            label: 'Staff',
            icon: UsersRound,
            onClick: () => {
              setRefresh(() => reload)
              setStaffOf(b)
            },
          },
        ]}
      />
      <BranchStaffDialog branch={staffOf} onClose={() => setStaffOf(null)} onChanged={() => refresh?.()} />
    </>
  )
}

export default Branches
